import { Status, Cardinality, PropertyTypeCategoryDTO } from '@/Enums/Enums';
import type {
    Dictionary,
    LVColumnRow,
    PropertyRow,
    PropertyGroupDropDownData,
} from '@/Datastructures';
import type {
    Member,
    ListView,
    DynamicAssoziation,
    DynamicAssoziationSide,
    Association,
    Base,
    DetailView,
    DetailViewGroup,
    DVGItem,
    LVColumn,
    MetadataEditor,
    Property,
    ResourceItem,
    TypeMeta,
} from '@/Typings';
import type {
    NodeData,
    GenericObject,
    TextBoxViewModel as TextBoxViewModelT,
    LinkViewModel as LinkViewModelT,
    ImageViewModel as ImageViewModelT,
    MLStringPopupBoxViewModel as MLStringPopupBoxViewModelT,
    MultilineTextBoxViewModel as MultilineTextBoxViewModelT,
} from 'radpresentation.htmlclient.defaultviewextensions';
import {
    Utilities,
    TreeUtilities,
    StorageHelper,
    RADApplication,
    ResourceManager,
    ComponentManager,
    ControlViewModel,
    NavigationService,
    LabelViewModel as LabelViewModelT,
    MessageBoxViewModel,
    CoreFrameDropDownTreeViewViewModel,
    LocatorHelper,
    GlobalConfiguration as Config,
} from 'radpresentation.htmlclient.defaultviewextensions';
import PropertyTabViewModel from '@/ViewModels/Controls/PropertyTabViewModel';
import AssociationTabViewModel from '@/ViewModels/Controls/AssociationTabViewModel';
import EditMaskTabViewModel from '@/ViewModels/Controls/EditMaskTabViewModel';
import ListViewTabViewModel from '@/ViewModels/Controls/ListViewTabViewModel';
import PropertiesGridViewModel from '@/ViewModels/Controls/PropertiesGridViewModel';
import DeletePropertyGroupMessageBoxViewModel from '@/ViewModels/Controls/DeletePropertyGroupMessageBoxViewModel';

const ExtensionEditor = 'ExtensionEditorJSON';

type ObservableNodes = KnockoutObservableArray<NodeData>;

class ExtensionEditorViewModel extends ControlViewModel {
    private LinkViewModel =
        ComponentManager.getViewModel<typeof LinkViewModelT>('LinkViewModel');
    private LabelViewModel =
        ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');
    private ImageViewModel =
        ComponentManager.getViewModel<typeof ImageViewModelT>('ImageViewModel');
    private MLStringPopupBoxViewModel = ComponentManager.getViewModel<
        typeof MLStringPopupBoxViewModelT
    >('MLStringPopupBoxViewModel');
    private MultilineTextBoxViewModel = ComponentManager.getViewModel<
        typeof MultilineTextBoxViewModelT
    >('MultilineTextBoxViewModel');

    // html-bindings:
    private isTypeSelected: KnockoutObservable<boolean>;
    private ErgName: MLStringPopupBoxViewModelT;
    private ErgNameLabel: LabelViewModelT;
    private ErgNamePlural: MLStringPopupBoxViewModelT;
    private ErgNamePluralLabel: LabelViewModelT;
    private PropertyGruppenLabel: LabelViewModelT;
    private PropertyGruppenDataSource = this.observableOrDefault(
        []
    ) as ObservableNodes;
    private PropertyGruppenSelection = this.observableOrDefault(
        []
    ) as KnockoutObservableArray<string>;
    private PropertyGruppen: CoreFrameDropDownTreeViewViewModel;
    private propertiesGrid: PropertiesGridViewModel;
    private PropertyGruppenCreateLink: LinkViewModelT;
    private PropertyGruppenDeleteLink: LinkViewModelT;
    private PropertyGruppenEditLink: LinkViewModelT;
    private propertyTabViewModel: PropertyTabViewModel;
    private associationTabViewModel: AssociationTabViewModel;
    private editMaskTabViewModel: EditMaskTabViewModel;
    private listViewTabViewModel: ListViewTabViewModel;

    public tabControlsAreFocusable: KnockoutObservable<boolean> =
        this.observableOrDefault(false);
    public shouldShowPropertyTab: KnockoutObservable<boolean> =
        this.observableOrDefault(true);
    public shouldShowAnyTab: KnockoutObservable<boolean> =
        this.observableOrDefault(true);

    // this view models properties
    private genericObject: GenericObject;
    private selectedType: KnockoutObservable<string>;
    private selectedDetailViewGroup: DetailViewGroup;
    private selectedPropertyRows: KnockoutObservableArray<PropertyRow>;
    private currentLanguage: string;
    private isTypeChange: boolean = false;
    private subscriptions: Array<KnockoutSubscription> = [];

    private selectedDetailViewGroupObs: KnockoutObservable<DetailViewGroup> =
        this.observableOrDefault(null);

    // data structures depending on json dto
    private typesById: Dictionary<TypeMeta> = {};
    private dtosByTypeId: Dictionary<MetadataEditor> = {};
    public resourceItemsByErgName: Dictionary<{
        DE?: ResourceItem;
        EN?: ResourceItem;
    }> = {};
    private detailViewGroupsByTypeId: Dictionary<Array<DetailViewGroup>> = {};
    private listViewColumnsByTypeId: Dictionary<Array<LVColumn>> = {};

    private newObjectsCount = 0;

    public preventCancel: KnockoutObservable<boolean> =
        this.observableOrDefault(false);

    public getNextPdoId(): string {
        return (++this.newObjectsCount * -1).toString();
    }

    public getTypeMeta(): TypeMeta {
        let selectedType = this.selectedType();
        let dto = this.dtosByTypeId[selectedType];
        let type = this.typesById[selectedType] as TypeMeta;

        return type;
    }

    public refreshPropertiesGrid() {
        this.propertiesGrid.refreshGrid();
    }

    private getUngroupedGroup(): DetailViewGroup {
        let dto = this.dtosByTypeId[this.selectedType()];
        let detailView = dto.DetailViews[dto.SubjectDetailView];
        let detailViewGroups = detailView.DetailViewGroups;

        let ungroupedGroup = TreeUtilities.getNodeByPredicate(
            detailViewGroups,
            group => Utilities.endsWith(group.Name, 'UNGROUPED'),
            'ChildNodes'
        );
        return ungroupedGroup;
    }

    private selectUngroupedGroup() {
        let ungroupedGroup = this.getUngroupedGroup();

        this.PropertyGruppenSelection([ungroupedGroup.PDO_ID.toString()]);
        this.PropertyGruppenDataSource.valueHasMutated();
    }

    public constructor(protected params: any) {
        super(params);

        this.currentLanguage = RADApplication.getInstance().currentLanguage();
        this.selectedType = this.observableOrDefault('');
        this.isTypeSelected = this.observableOrDefault(false);
        this.selectedPropertyRows = this.observableOrDefault(
            []
        ) as KnockoutObservableArray<PropertyRow>;

        this.createPropertyGroupsDropDown();
        this.createPropertyGruppenLinks();
        this.createPropertiesGrid();

        this.propertyTabViewModel = new PropertyTabViewModel(
            LocatorHelper.buildLocator(this.locator, 'prop_tab')
        );
        this.associationTabViewModel = new AssociationTabViewModel(
            LocatorHelper.buildLocator(this.locator, 'asso_tab')
        );
        this.editMaskTabViewModel = new EditMaskTabViewModel(
            LocatorHelper.buildLocator(this.locator, 'editmask_tab')
        );
        this.listViewTabViewModel = new ListViewTabViewModel(
            LocatorHelper.buildLocator(this.locator, 'listview_tab')
        );

        this.propertyTabViewModel.context = this;
        this.propertyTabViewModel.editMaskTabViewModel =
            this.editMaskTabViewModel;
        this.propertyTabViewModel.listViewTabViewModel =
            this.listViewTabViewModel;

        this.associationTabViewModel.context = this;
        this.associationTabViewModel.editMaskTabViewModel =
            this.editMaskTabViewModel;

        this.editMaskTabViewModel.context = this;
        this.editMaskTabViewModel.propertyTabViewModel =
            this.propertyTabViewModel;
        this.editMaskTabViewModel.associationTabViewModel =
            this.associationTabViewModel;

        this.ErgNameLabel = new this.LabelViewModel({
            ergName: 'RADEM.RADEXTENSIONMANAGER.ERGNAMELABEL',
            postfix: ' *',
        });
        this.ErgNamePluralLabel = new this.LabelViewModel({
            ergName: 'RADEM.RADEXTENSIONMANAGER.ERGNAMEPLURALIZEDLABEL',
            postfix: ' *',
        });

        this.ErgName = new this.MLStringPopupBoxViewModel({
            isMultiline: false,
            isEnabled: true,
            locator: LocatorHelper.buildLocator(this.locator, 'ergNamePopup'),
            multilineTextBox: new this.MultilineTextBoxViewModel({
                isMultiline: false,
                isEnabled: true,
                locator: LocatorHelper.buildLocator(
                    this.locator,
                    'ergNameMLTextBox'
                ),
            }),
            isRequired: true,
            useIsEachLanguageEqual: true,
            isEachLanguageEqual: true,
        });

        this.registerErgNameChanges(this.ErgName, type => type.ErgName);

        this.ErgNamePlural = new this.MLStringPopupBoxViewModel({
            isMultiline: false,
            isEnabled: true,
            locator: LocatorHelper.buildLocator(
                this.locator,
                'ergNamePluralPopup'
            ),
            multilineTextBox: new this.MultilineTextBoxViewModel({
                isMultiline: false,
                isEnabled: true,
                locator: LocatorHelper.buildLocator(
                    this.locator,
                    'ergNamePluralMLTextBox'
                ),
            }),
            isRequired: true,
            useIsEachLanguageEqual: true,
            isEachLanguageEqual: true,
        });

        this.registerErgNameChanges(
            this.ErgNamePlural,
            type => type.ErgNamePluralized
        );

        this.listViewTabViewModel.context = this;

        this.propertiesGrid.onSelectionChange(selected => {
            let isEmptySelection = _.isEmpty(selected);

            let relatedData =
                this.propertiesGrid.getSelectedRowData()[0] || null;
            this.selectedPropertyRows(this.propertiesGrid.getSelectedRowData());

            let selectedType = this.selectedType();
            let dto = this.dtosByTypeId[selectedType];
            let dvgItem: DVGItem = null;

            if (!isEmptySelection) {
                let detailViewGroups =
                    this.detailViewGroupsByTypeId[selectedType];
                let selectedPropertyGroup = this.selectedDetailViewGroupObs();
                let detailViewGroup = detailViewGroups.filter(
                    dvGroup =>
                        dvGroup.PDO_ID.toString() ==
                        selectedPropertyGroup.PDO_ID.toString()
                )[0];

                dvgItem = detailViewGroup.Items.filter(
                    anyItem => anyItem.PDO_ID == selected[0]
                )[0];
            }

            this.shouldShowAnyTab(isEmptySelection || dvgItem.IsEditable);
            this.shouldShowPropertyTab(
                isEmptySelection ||
                    !this.isAssociationMeta(
                        this.getMetaByPdoId(dvgItem.Member.PDO_ID)
                    )
            );

            this.propertyTabViewModel.DVGItem = dvgItem;
            this.propertyTabViewModel.updateContent(relatedData);

            this.associationTabViewModel.DVGItem = dvgItem;
            this.associationTabViewModel.updateContent(relatedData);

            if (isEmptySelection) {
                this.propertyTabViewModel.disableAllControls();
                this.associationTabViewModel.disableAllControls();
            }

            this.editMaskTabViewModel.DVGItem = dvgItem;
            if (relatedData) relatedData.ChildNodes = dvgItem?.ChildNodes;

            this.editMaskTabViewModel.updateContent(relatedData);

            let listViewRows =
                this.listViewTabViewModel.ListViewGrid.dataGrid.data();
            let listViewSelectionUpdate = [];
            if (!isEmptySelection) {
                let matches = listViewRows.filter(
                    (row: LVColumnRow) => row.DisplayName == relatedData.Name
                );
                if (!_.isEmpty(matches)) {
                    listViewSelectionUpdate.push(matches[0].PDO_ID);
                }
            }

            this.listViewTabViewModel.ListViewGrid.dataGrid.selected(
                listViewSelectionUpdate
            );
        });

        /**
         * When selectedType changes:
         *  - propertyGroup dropdown is re-populated
         *  - ergonomic identifier is updated
         *  - listViewColumns data-structure is injected to association grid
         *  - list view association grid is re-populated
         *  - isTypeSelected (observable) is re-evaluated
         **/
        this.defineSubscription(this.selectedType, pdoId => {
            this.isTypeChange = true;

            this.updateAllViewModels();
        });

        this.defineComputed(() => {
            let canCreateProperty =
                !!this.selectedType() &&
                !_.isEmpty(this.PropertyGruppenSelection());

            this.propertiesGrid.createLink.isEnabled(canCreateProperty);
        });
    } // constructor ends

    private updateAllViewModels() {
        try {
            const pdoId = this.selectedType();
            let dto = this.dtosByTypeId[pdoId];
            let type = this.typesById[pdoId];

            this.propertiesGrid.metaData = dto;
            this.propertyTabViewModel.typeMeta = type;
            this.associationTabViewModel.typeMeta = type;

            // refreshPropertyGroupsDropDown
            this.refreshPropertyGroupsDropDown();

            // update ergonomic identifier
            this.updateErgNames(this.ErgName, type.ErgName);
            this.updateErgNames(this.ErgNamePlural, type.ErgNamePluralized);

            // listView and listViewColumns data- structure are injected to association grid
            let lvColumnDtos = this.listViewColumnsByTypeId[pdoId];
            let listView = dto.ListViews[dto.SubjectListView];
            this.listViewTabViewModel.listViewColumns = lvColumnDtos;
            this.listViewTabViewModel.listView = listView;

            // re-populate list view association grid
            let dataUpdate = lvColumnDtos.map(
                this.convertLvColumnToListViewRow.bind(this)
            ) as Array<LVColumnRow>;

            // populate grid
            this.listViewTabViewModel.populateGrid(dataUpdate);

            // update propertyTabViewModel
            this.propertyTabViewModel.ResourceItems = dto.ResourceItems;
            this.associationTabViewModel.ResourceItems = dto.ResourceItems;

            // re-evaluate isTypeSelected
            this.isTypeSelected(Utilities.isNotBlank(pdoId));
        } finally {
            this.isTypeChange = false;
        }
    }

    private updateErgNames(
        viewModel: MLStringPopupBoxViewModelT,
        ergName: string
    ): void {
        const ri = this.resourceItemsByErgName[ergName.toLocaleLowerCase()];
        const dict = {};
        _.keys(ri).forEach(key => {
            dict[key] = ri[key].Value;
        });

        // Pruefen, ob Flag "isLanguageEqual" gesetzt werden muss
        let isEachLanguageEqual = true;
        const persistentTranslationCount = _.keys(dict).length;
        const persistentTranslationsCountIsDifferentToLanguageCount =
            persistentTranslationCount > 0 &&
            viewModel.groups().length !== persistentTranslationCount;
        const uniqueValues = _.uniq(_.values(dict));
        if (
            persistentTranslationsCountIsDifferentToLanguageCount ||
            uniqueValues.length > 1
        )
            isEachLanguageEqual = false;

        viewModel.isEachLanguageEqual(isEachLanguageEqual);
        viewModel.groups().forEach(group => {
            let language = group.language;
            let value = this.resolveErgName(ergName, language);

            if (group.language === this.currentLanguage) {
                viewModel.multilineTextBox.value(value);
            } else {
                group.liveValue(value);
            }
        });
    }

    private registerErgNameChanges(
        viewModel: MLStringPopupBoxViewModelT,
        ergNameResolver: (type: TypeMeta) => string
    ): void {
        viewModel.groups().forEach(group => {
            this.defineSubscription(group.liveValue, liveValueUpdate => {
                if (this.isTypeChange) return;

                const selectedType = this.selectedType();
                const type = this.typesById[selectedType];
                this.updateResourceItem(
                    ergNameResolver(type),
                    group.language,
                    liveValueUpdate
                );
            });
        });
    }

    protected disposeInternal(): void {
        this.propertyTabViewModel.dispose();
        this.associationTabViewModel.dispose();
        this.editMaskTabViewModel.dispose();
        this.listViewTabViewModel.dispose();

        if (!_.isEmpty(this.subscriptions)) {
            this.subscriptions.forEach(s => s.dispose());
        }

        super.disposeInternal();
    }

    private convertDVGroupToTreeNode(group: DetailViewGroup) {
        if (group.Status == Status.Deleted) return null;

        return {
            key: group.PDO_ID + '',
            title: this.resolveErgName(group.ErgName),
            children: group.ChildNodes
                ? group.ChildNodes.map(childGroup =>
                      this.convertDVGroupToTreeNode(childGroup)
                  ).filter(x => x != null)
                : [],
            expanded: !_.isEmpty(group.ChildNodes),
        } as NodeData;
    }

    /**
     * Aktualisiert die Baumdarstellung der DetailViewGroups
     * @param resetSelection    Bestimmt, ob die aktuelle Selektion zurueckgesetzt werden soll
     */
    private refreshPropertyGroupsDropDown(resetSelection: boolean = true) {
        let dto = this.dtosByTypeId[this.selectedType()];
        let detailView = dto.DetailViews[dto.SubjectDetailView];
        let detailViewGroups = detailView.DetailViewGroups;

        let treeData = detailViewGroups
            .map(group => this.convertDVGroupToTreeNode(group))
            .filter(x => x != null);
        let defaultGroup = detailViewGroups.filter(e =>
            Utilities.endsWith(e.Name, '.DEFAULT')
        )[0];

        let newSelection: string;

        if (resetSelection)
            newSelection = (
                defaultGroup ? defaultGroup.PDO_ID : detailViewGroups[0].PDO_ID
            ).toString();
        else newSelection = this.PropertyGruppenSelection()[0];

        let selectionChanged =
            newSelection != this.PropertyGruppenSelection()[0];

        if (selectionChanged) {
            this.PropertyGruppenSelection([newSelection]);
        } else {
            this.onPropertyGroupSelectionChanged([newSelection]);
        }

        this.PropertyGruppenDataSource(treeData);
        this.activatePropertyGruppenNode(this.PropertyGruppenSelection()[0]);
    }

    public findLVColumnRowByName(name: string): LVColumnRow {
        let listViewRows =
            this.listViewTabViewModel.ListViewGrid.dataGrid.data();

        return Utilities.findFirst(
            listViewRows,
            (row: LVColumnRow) => row.DisplayName == name
        );
    }

    public findLVColumnByName(name: string): LVColumn {
        let dto = this.dtosByTypeId[this.selectedType()];
        let allColumns = this.getListViewColumns(dto);

        return Utilities.findFirst(allColumns, (column: LVColumn) =>
            Utilities.equalsIgnoreCase(column.Name, name)
        );
    }

    private convertLvColumnToListViewRow(columnDto: LVColumn): LVColumnRow {
        let displayName = this.convertListViewRowNameToDisplayName(
            columnDto.Name
        );

        return {
            lvColumnDto: columnDto,
            PDO_ID: columnDto.PDO_ID,
            Name: columnDto.Name,
            DisplayName: displayName,
            Typ: this.resolveColumnType(columnDto),
            Visibility: columnDto.IsVisible,
            VisibilityObs: this.observableOrDefault(
                columnDto.IsVisible == true
            ),
            Sortability: columnDto.Sortable,
            SortabilityObs: this.observableOrDefault(
                columnDto.Sortable == true
            ),
            Filterability: columnDto.IsFilterable,
            FilterabilityObs: this.observableOrDefault(
                columnDto.IsFilterable == true
            ),
        } as LVColumnRow;
    }

    private convertListViewRowNameToDisplayName(name: string): string {
        let displayName = name;
        if (Utilities.endsWith(name, '.Value')) {
            displayName = name.substring(0, name.length - '.Value'.length);
        } else if (Utilities.endsWith(name, '.ObjectIdentValue')) {
            displayName = name.substring(
                0,
                name.length - '.ObjectIdentValue'.length
            );
        } else if (Utilities.endsWith(name, '.FileName')) {
            displayName = name.substring(0, name.length - '.FileName'.length);
        } else if (Utilities.endsWith(name, '.ListViewValue')) {
            displayName = name.substring(
                0,
                name.length - '.ListViewValue'.length
            );
        }
        return displayName;
    }

    private convertDVGItemToPropertiesRow(item: DVGItem): PropertyRow {
        let member = item.Member;
        let propertyOrAssociation = this.getMetaByPdoId(member.PDO_ID);
        let property = propertyOrAssociation as Property;
        let association = propertyOrAssociation as Association;

        let dv_uiGenerator = item.UIGenerator || '';
        let sv_uiGenerator = item.UIGeneratorSummaryView || '';

        let has_dv_uiGeneratorParameter = false;
        let childUIGenerator = item.ChildNodes?.filter(node => {
            if (node.Member) return node.Member.Name === 'Value';

            return node.Name === 'Value';
        })[0]?.UIGenerator;

        let isMLString =
            property.DotNetTypeName ===
            'RAD.RADFoundation.ProblemDomain.MLString';

        if (isMLString) {
            has_dv_uiGeneratorParameter = Utilities.contains(
                childUIGenerator,
                '?'
            );
        } else {
            has_dv_uiGeneratorParameter = Utilities.contains(
                dv_uiGenerator,
                '?'
            );
        }

        let has_sv_uiGeneratorParameter = Utilities.contains(
            sv_uiGenerator,
            '?'
        );

        let isNewItem = item.Status == Status.New;

        let ergNames = !member.ErgName
            ? {}
            : this.resourceItemsByErgName[member.ErgName.toLowerCase()];
        let helptexts = !member.Helptext
            ? {}
            : this.resourceItemsByErgName[member.Helptext.toLowerCase()];
        let tooltips = !member.Tooltip
            ? {}
            : this.resourceItemsByErgName[member.Tooltip.toLowerCase()];
        let constraintMessages = !property.ConstraintMessage
            ? {}
            : this.resourceItemsByErgName[
                  property.ConstraintMessage.toLowerCase()
              ];

        let extractValuesByLanguage = (
            resourceItemByLanguage: Dictionary<ResourceItem>
        ) => {
            let valuesByLanguage = {};
            _.keys(resourceItemByLanguage).forEach(
                language =>
                    (valuesByLanguage[language] =
                        (resourceItemByLanguage[language] &&
                            resourceItemByLanguage[language].Value) ||
                        '')
            );
            return valuesByLanguage;
        };

        let dvParameterGenerator = childUIGenerator
            ? childUIGenerator
            : dv_uiGenerator;

        let uiGenerator = isMLString
            ? item.UIGenerator
            : dvParameterGenerator.split('?')[0];
        let parameter = isMLString
            ? childUIGenerator.split('?')[1]
            : dvParameterGenerator.split('?')[1];

        return {
            // by Base
            PDO_ID: item.PDO_ID,

            // by DVGItem
            IsVisible: item.IsVisible,
            IsVisibleOnSummaryView: item.IsVisibleOnSummaryView,
            UIGenerator: uiGenerator,
            UIGeneratorDvParameter: has_dv_uiGeneratorParameter
                ? parameter
                : '',
            UIGeneratorSummaryView: has_sv_uiGeneratorParameter
                ? sv_uiGenerator.split('?')[0]
                : sv_uiGenerator,
            UIGeneratorSvParameter: has_sv_uiGeneratorParameter
                ? sv_uiGenerator.split('?')[1]
                : '',

            // by Property
            DotNetTypeName: property.DotNetTypeName,
            MaxLength: property.Length ? parseInt(property.Length) : 0,

            ErgNameByLanguage: isNewItem
                ? {}
                : extractValuesByLanguage(ergNames),
            HelpByLanguage: isNewItem ? {} : extractValuesByLanguage(helptexts),
            TooltipByLanguage: isNewItem
                ? {}
                : extractValuesByLanguage(tooltips),
            ConstraintMessageByLanguage: isNewItem
                ? {}
                : extractValuesByLanguage(constraintMessages),

            ConstraintValue: property.Constraint || '',
            Required: property.Required || false,
            RequiredWhen: property.RequiredWhen || '',

            // by Association
            Cardinality: Utilities.isNullOrUndefined(association.Cardinality)
                ? null
                : association.Cardinality,
            Mandatory: association.Mandatory || false,
            MandatoryWhen: association.MandatoryWhen || '',

            // by BaseMetadata
            Name: member.Name,
            Dynamisch: member.IsDynamic,

            //
            Indexable: property.Indexable || false,
            Typ: property.DotNetTypeName
                ? this.lookupDotNetTypeName(property.DotNetTypeName)
                : this.resolveTypeByMemberPdoId(member.PDO_ID),
            tooltip: this.buildCellTooltip(
                property.DotNetTypeName ??
                    this.resolveTypeByMemberPdoId(member.PDO_ID)
            ),
            Status: item.Status,

            isValid: this.observableOrDefault(true),
        } as PropertyRow;
    }

    public buildCellTooltip(value: string): string {
        if (value?.startsWith('System.Nullable`1[[System.DateTime'))
            return 'Nullable<DateTime>';

        return value;
    }

    private createPropertyGroupsDropDown() {
        this.PropertyGruppenLabel = this.LabelViewModel.create(
            'RADEM.RADEXTENSIONMANAGER.PROPERTYGROUPSLABEL'
        );

        let propertyGroupEditMode = false;

        this.PropertyGruppen = new CoreFrameDropDownTreeViewViewModel({
            dataSource: this.PropertyGruppenDataSource,
            selectedKeys: this.PropertyGruppenSelection,
            isMultiselect: false,
            hasCheckboxes: false,
            isEnabled: this.isEnabled,
            locator: LocatorHelper.buildLocator(
                this.locator,
                '.prop_groups_tree_drop_down'
            ),
        });

        this.defineSubscription(
            this.PropertyGruppenSelection,
            this.onPropertyGroupSelectionChanged.bind(this)
        );
    }

    private onPropertyGroupSelectionChanged(
        selectedGroupPdoIds: Array<string>
    ): void {
        let selectedGroupPdoId = selectedGroupPdoIds[0];
        let selectedType = this.selectedType();
        let detailViewGroups = this.detailViewGroupsByTypeId[selectedType];
        let byPdoId = (group: DetailViewGroup) =>
            group.PDO_ID == selectedGroupPdoId;
        let detailViewGroup = Utilities.findFirst(detailViewGroups, byPdoId);
        let detailViewGroupItems = detailViewGroup.Items.filter(
            item => item.Status !== Status.Deleted
        );

        let dataUpdate = detailViewGroupItems.map<PropertyRow>(
            this.convertDVGItemToPropertiesRow.bind(this)
        );
        dataUpdate = dataUpdate.filter(x => x != null);

        this.propertiesGrid.updateData(dataUpdate);
        this.propertiesGrid.select([]);
        this.selectedDetailViewGroupObs(detailViewGroup);
        this.PropertyGruppenDeleteLink.isEnabled(detailViewGroup.IsDynamic);
    }

    private createPropertyGruppenLinks() {
        this.PropertyGruppenCreateLink = new this.LinkViewModel({
            image: new this.ImageViewModel({
                source: Config.inlineImages.OBJ_CREATE,
                alternativeText: new LabelViewModelT({
                    ergName: 'RADEM.RADEXTENSIONMANAGER.CREATE_PROPERTY_GROUP',
                }),
            }),
            tooltip: this.LabelViewModel.create(
                'RADEM.RADEXTENSIONMANAGER.CREATE_PROPERTY_GROUP'
            ),
            isEnabled: true,
            isVisible: true,
            onClick: this.openCreatePropertyGroupWindow.bind(this),
            locator: LocatorHelper.buildLocator(
                this.locator,
                '.create_prop_group'
            ),
        });

        this.PropertyGruppenDeleteLink = new this.LinkViewModel({
            image: new this.ImageViewModel({
                source: Config.inlineImages.OBJ_DELETE,
                alternativeText: new LabelViewModelT({
                    ergName: 'RADEM.RADEXTENSIONMANAGER.DELETE_PROPERTY_GROUP',
                }),
            }),
            tooltip: this.LabelViewModel.create(
                'RADEM.RADEXTENSIONMANAGER.DELETE_PROPERTY_GROUP'
            ),
            isEnabled: true,
            isVisible: true,
            onClick: this.openDeletePropertyGroupWindow.bind(this),
            locator: LocatorHelper.buildLocator(
                this.locator,
                '.delete_prop_group'
            ),
        });

        DeletePropertyGroupMessageBoxViewModel.yesCallback =
            this.deletePropertyGroup.bind(this);

        this.PropertyGruppenEditLink = new this.LinkViewModel({
            image: new this.ImageViewModel({
                source: Config.inlineImages.OBJ_EDIT,
                alternativeText: new LabelViewModelT({
                    ergName: 'RADEM.RADEXTENSIONMANAGER.EDIT_PROPERTY_GROUP',
                }),
            }),
            tooltip: this.LabelViewModel.create(
                'RADEM.RADEXTENSIONMANAGER.EDIT_PROPERTY_GROUP'
            ),
            isEnabled: true,
            isVisible: true,
            onClick: this.openEditPropertyGroupWindow.bind(this),
            locator: LocatorHelper.buildLocator(
                this.locator,
                '.edit_prop_group'
            ),
        });
    }

    private openCreatePropertyGroupWindow() {
        let loadTuple = true;
        let params = {
            callback: this.createNewPropertyGroup.bind(this),
            takenNames: _.pluck(
                this.getAllNodes(this.PropertyGruppenDataSource()),
                'title'
            ),
            locator: LocatorHelper.buildLocator(
                this.locator,
                '.create_prop_group'
            ),
        };

        NavigationService.getInstance().loadViewFactoryIsolated(
            'CreatePropertyGroup',
            params,
            loadTuple
        );
    }

    private getParentsByDetailViewGroup(): Dictionary<DetailViewGroup> {
        let detailView = this.getCurrentDetailView();
        let detailViewGroups = detailView.DetailViewGroups;

        let datastructure = {} as Dictionary<DetailViewGroup>;

        let parse = (
            groups: Array<DetailViewGroup>,
            parent: DetailViewGroup
        ): void => {
            (groups || []).forEach((group: DetailViewGroup) => {
                datastructure[group.PDO_ID] = parent;
                parse(group.ChildNodes, group);
            });
        };

        parse(detailViewGroups, null);

        return datastructure;
    }

    private openEditPropertyGroupWindow() {
        let detailView = this.getCurrentDetailView();
        let topLevelGroups = detailView.DetailViewGroups;
        let selectedGroup = this.selectedDetailViewGroupObs();

        let newParentExclusion = this.getAllDetailViewGroups([selectedGroup]); // can not be new parents
        let allGroups = this.getAllDetailViewGroups(topLevelGroups);

        let parentByDetailViewGroupPdoId = this.getParentsByDetailViewGroup();
        let parent =
            parentByDetailViewGroupPdoId[selectedGroup.PDO_ID.toString()];

        let newParentCandidates = allGroups.filter(
            anyGroup => !_.contains(newParentExclusion, anyGroup)
        );
        let parentGroupData = newParentCandidates.map(anyGroup => {
            return {
                caption: this.resolveErgName(anyGroup.ErgName),
                value: anyGroup.PDO_ID,
            };
        });

        let propertyGroupsSortingGridData = (
            parent != null ? parent.ChildNodes : topLevelGroups
        ).map(anyGroup => {
            return {
                pdoId: anyGroup.PDO_ID.toString(),
                objectIdentValue: this.resolveErgName(anyGroup.ErgName),
            };
        });

        let params = {
            callback: this.editPropertyGroup.bind(this),

            currentParentGroup: parent == null ? null : parent.PDO_ID,
            propertyGruppenData: this.PropertyGruppenDataSource(),
            selectedGroupPdoId: selectedGroup.PDO_ID.toString(),
            parentGroupData: parentGroupData,

            isVisible: this.selectedDetailViewGroupObs().IsVisible,
            uiGenerator: this.selectedDetailViewGroupObs().UIGenerator,

            locator: LocatorHelper.buildLocator(
                this.locator,
                '.edit_prop_group_box'
            ),
        };

        let loadTuple = true;
        NavigationService.getInstance().loadViewFactoryIsolated(
            'EditPropertyGroup',
            params,
            loadTuple
        );
    }

    private editPropertyGroup(
        propertyGruppenData: Array<NodeData>,
        isVisible: boolean,
        uiGenerator: string,
        newParentGroupPdoId: string
    ) {
        let detailView = this.getCurrentDetailView();
        let topLevelGroups = detailView.DetailViewGroups;
        let selectedGroup = this.selectedDetailViewGroupObs();
        let allDetailViewGroups = this.getAllDetailViewGroups(topLevelGroups); // flat

        let parentByDetailViewGroupPdoId = this.getParentsByDetailViewGroup();
        let oldParent =
            parentByDetailViewGroupPdoId[selectedGroup.PDO_ID.toString()];
        let newParent =
            newParentGroupPdoId != null
                ? Utilities.findFirst(
                      allDetailViewGroups,
                      (g: DetailViewGroup) => g.PDO_ID == newParentGroupPdoId
                  )
                : null;

        // update view
        let oldTreeDataSource = this.PropertyGruppenDataSource();
        this.PropertyGruppenDataSource(propertyGruppenData);
        if (oldTreeDataSource.length == propertyGruppenData.length) {
            this.PropertyGruppenDataSource.valueHasMutated();
        }
        this.activatePropertyGruppenNode(selectedGroup.PDO_ID.toString());

        this.moveDetailViewGroup(selectedGroup, oldParent, newParent);

        // sync new order to json
        detailView.DetailViewGroups = this.sortDetailViewGroups(
            this.PropertyGruppenDataSource(),
            allDetailViewGroups
        );
        Status.setChanged(detailView);

        // sync visible-state to json
        selectedGroup.IsVisible = isVisible;

        // update uiGenerator
        selectedGroup.UIGenerator = uiGenerator;

        Status.setChanged(selectedGroup);
    }

    /**
     * Verschiebt eine DetailViewGroup
     * @param dvg           Zu verschiebende DetailViewGroup
     * @param oldParent     Aktuelle Eltern-DetailViewGroup
     * @param newParent     Zukuenftige Eltern-DetailViewGroup
     */
    private moveDetailViewGroup(
        dvg: DetailViewGroup,
        oldParent: DetailViewGroup,
        newParent: DetailViewGroup
    ): void {
        let detailView = this.getCurrentDetailView();
        let parentPdoId = oldParent != null ? oldParent.PDO_ID : null;
        let newParentPdoID = newParent != null ? newParent.PDO_ID : null;

        if (parentPdoId != newParentPdoID) {
            // remove from old-parents-children

            if (oldParent == null) {
                detailView.DetailViewGroups =
                    detailView.DetailViewGroups.filter(
                        g => g.PDO_ID != dvg.PDO_ID
                    );
                Status.setChanged(detailView);
            } else {
                oldParent.ChildNodes = oldParent.ChildNodes.filter(
                    g => g.PDO_ID != dvg.PDO_ID
                );
                Status.setChanged(oldParent);
            }

            // add to new-parents-children
            if (newParent != null) {
                newParent.ChildNodes.push(dvg);
                Status.setChanged(newParent);
            } else {
                detailView.DetailViewGroups.push(dvg);
                Status.setChanged(detailView);
            }

            Status.setChanged(dvg);
        }
    }

    private sortDetailViewGroups(
        nodes: Array<NodeData>,
        allDetailViewGroups: Array<DetailViewGroup>
    ) {
        return (nodes || []).map(node => {
            let group = Utilities.findFirst(
                allDetailViewGroups,
                anyGroup => anyGroup.PDO_ID == node.key
            );
            Status.setChanged(group);
            group.ChildNodes = this.sortDetailViewGroups(
                node.children,
                allDetailViewGroups
            );

            return group;
        });
    }

    private activatePropertyGruppenNode(pdoId: string | number) {
        let currentlyActiveNode = this.PropertyGruppen.activateNodeByKey();
        if (currentlyActiveNode == pdoId) {
            this.PropertyGruppen.activateNodeByKey.valueHasMutated();
        } else {
            this.PropertyGruppen.activateNodeByKey(pdoId.toString());
        }
    }

    private createNewPropertyGroup(
        newGroupName: string,
        newGroupMlString: { [key: string]: string }
    ): void {
        let dto = this.dtosByTypeId[this.selectedType()];
        let type = dto.Types[dto.SubjectPDTypeMeta];
        let typeName = type.Name;
        let typePdoId = type.PDO_ID;
        let detailView = this.getCurrentDetailView();

        let completeGroupName = [typeName, newGroupName.toUpperCase()].join(
            '.'
        );
        let newGroupErgNameKey = [typeName, newGroupName.toUpperCase()].join(
            '_DVG_'
        );

        let newDetailViewGroup = {} as DetailViewGroup;
        newDetailViewGroup.Status = Status.New;
        newDetailViewGroup.Name = completeGroupName;
        newDetailViewGroup.UIGenerator =
            'ExpanderGroup?Layout=OneColumnLayout&IsCollapsed=false';
        newDetailViewGroup.IsVisible = true;
        newDetailViewGroup.ErgName = newGroupErgNameKey;
        newDetailViewGroup.PDO_ID = this.getNextPdoId();
        newDetailViewGroup.Items = [];
        newDetailViewGroup.IsDynamic = true;
        newDetailViewGroup.ChildNodes = [];

        for (let languageKey in newGroupMlString) {
            if (newGroupMlString.hasOwnProperty(languageKey)) {
                const dvgResourceItem = {} as ResourceItem;
                dvgResourceItem.Value = newGroupMlString[languageKey];
                dvgResourceItem.ResourceID = newGroupErgNameKey;
                dvgResourceItem.Status = Status.New;
                dvgResourceItem.ISO6391 = languageKey;
                dvgResourceItem.PDO_ID = this.getNextPdoId();
                dto.ResourceItems[dvgResourceItem.PDO_ID] = dvgResourceItem;
            }
        }

        // update json and datastructure
        detailView.DetailViewGroups.push(newDetailViewGroup);
        this.detailViewGroupsByTypeId[typePdoId] =
            this.getDetailViewGroups(dto);

        // update view
        let newGroup = {
            key: newDetailViewGroup.PDO_ID,
            title: newGroupName,
        } as NodeData;

        this.PropertyGruppenSelection([newDetailViewGroup.PDO_ID]);
        this.PropertyGruppenDataSource(
            this.PropertyGruppenDataSource().concat([newGroup])
        );
        this.activatePropertyGruppenNode(newDetailViewGroup.PDO_ID);

        this.propertiesGrid.select([]);
    }

    private openDeletePropertyGroupWindow() {
        let selectedPropertyGroupName = this.selectedDetailViewGroupObs().Name;
        let type = this.typesById[this.selectedType()];
        let selectedTypeName = type.Name;
        let locator = LocatorHelper.buildLocator(
            this.locator,
            '.delete_prop_group'
        );

        RADApplication.getInstance().showMessageBox(
            new DeletePropertyGroupMessageBoxViewModel(
                selectedPropertyGroupName,
                selectedTypeName,
                locator
            ) as MessageBoxViewModel
        );
    }

    private deletePropertyGroup() {
        let dto = this.dtosByTypeId[this.selectedType()];
        let type = dto.Types[dto.SubjectPDTypeMeta];
        let typeId = type.PDO_ID;
        let detailView = this.getCurrentDetailView();
        let groupToDelete = this.selectedDetailViewGroupObs();
        let keyOfGroupToDelete = groupToDelete.PDO_ID.toString();
        let isNew = groupToDelete.Status == Status.New;

        let groupToDeleteNode = TreeUtilities.getNodeByKey(
            this.PropertyGruppenDataSource(),
            keyOfGroupToDelete
        );
        let parent = TreeUtilities.getParentByKey(
            this.PropertyGruppenDataSource(),
            keyOfGroupToDelete
        );
        let isRootLevelNode = parent == null;
        let siblings = isRootLevelNode
            ? this.PropertyGruppenDataSource()
            : parent.children;
        let index = _.findIndex(
            siblings,
            anyNodeData => anyNodeData.key == keyOfGroupToDelete
        );

        let isSingleChild = siblings.length == 1;
        let isFirstNode = index == 0;
        let previousNode = isFirstNode ? null : siblings[index - 1];
        let firstChildNode =
            groupToDeleteNode.children && groupToDeleteNode.children.length > 0
                ? groupToDeleteNode.children[0]
                : null;
        let nextNode = firstChildNode || siblings[index + 1];

        // update view
        let newSelectedElement = isSingleChild
            ? parent
            : nextNode || previousNode;
        if (!isRootLevelNode) {
            parent.expanded = !isSingleChild;
        }

        // Verschieben der untergeordneten Properties in die Gruppe "UNGROUPED"
        if (groupToDelete.Items.length > 0) {
            this.propertiesGrid.moveDVGItems(
                groupToDelete.Items,
                groupToDelete,
                this.getUngroupedGroup()
            );
        }

        // Verschieben der untergeordneten Gruppen auf oberste Ebene
        if (groupToDelete.ChildNodes.length > 0) {
            let dvGroups = groupToDelete.ChildNodes;
            for (let x = 0; x < dvGroups.length; x++) {
                this.moveDetailViewGroup(dvGroups[x], groupToDelete, null);
            }
        }

        this.PropertyGruppenSelection([newSelectedElement.key]);
        this.PropertyGruppen.removeNodeByKey(keyOfGroupToDelete);
        this.PropertyGruppenDataSource.valueHasMutated();

        // update json
        let parentDetailViewGroup =
            this.getParentsByDetailViewGroup()[keyOfGroupToDelete];
        let isRootLevelGroup = parentDetailViewGroup == null;

        if (isNew) {
            if (isRootLevelGroup) {
                detailView.DetailViewGroups =
                    detailView.DetailViewGroups.filter(
                        group => group.PDO_ID != groupToDelete.PDO_ID
                    );
            } else {
                parentDetailViewGroup.ChildNodes =
                    parentDetailViewGroup.ChildNodes.filter(
                        group => group.PDO_ID != groupToDelete.PDO_ID
                    );
            }

            this.detailViewGroupsByTypeId[typeId] =
                this.getDetailViewGroups(dto);
        } else {
            groupToDelete.Status = Status.Deleted;
            Status.setChanged(
                isRootLevelGroup ? detailView : parentDetailViewGroup
            );
        }

        this.deleteResourceItems([groupToDelete.ErgName]);

        this.refreshPropertyGroupsDropDown(false);
    }

    private createPropertiesGrid() {
        this.propertiesGrid = new PropertiesGridViewModel({
            selectedDetailViewGroup: this.selectedDetailViewGroupObs,
            deletePropertyCallback: this.deleteProperty.bind(this),
            createDynamicAssociationCallback:
                this.createDynamicAssociationCallback.bind(this),
            addPropertyCallback: this.addProperty.bind(this),
            propertyGroupsData: this.PropertyGruppenDataSource,
            locator: LocatorHelper.buildLocator(this.locator, '.props_grid'),
        });
    }

    public resolveColumnType(column: LVColumn): string {
        if (column.IsAssoziation == false) {
            let property = this.getPropertyByLvColumn(column);
            return this.lookupDotNetTypeName(
                property ? property.DotNetTypeName : column.DotNetTypeName
            );
        }

        let type = this.typesById[this.selectedType()];
        let test = metaDto =>
            metaDto.Name === column.Name ||
            metaDto.Name === column.Name.substring(0, column.Name.indexOf('.'));

        let filtered = type.Assoziations.filter(test);
        if (_.isEmpty(filtered)) {
            return '-';
        }

        return 'To' + Cardinality[filtered[0].Cardinality] + 'Association';
    }

    private resolveTypeByMemberPdoId(memberPdoId: string): string {
        let meta = this.getMetaByPdoId(memberPdoId);
        if (!meta) {
            return '';
        }

        if (meta['Cardinality'] !== undefined) {
            return (
                'To' +
                Cardinality[(meta as Association).Cardinality] +
                'Association'
            );
        } else {
            return this.lookupDotNetTypeName((meta as Property).DotNetTypeName);
        }
    }

    private isAssociationMeta(meta: Property | Association): boolean {
        return meta['Cardinality'] !== undefined;
    }

    public getPropertyMetaByPdoId(pdoId: string): Property {
        let type = this.typesById[this.selectedType()];
        let test = metaDto => metaDto.PDO_ID == pdoId;

        return Utilities.findFirst(type.Properties, test);
    }

    public getAssociationMetaByPdoId(pdoId: string): Association {
        let type = this.typesById[this.selectedType()];
        let test = metaDto => metaDto.PDO_ID == pdoId;

        return Utilities.findFirst(type.Assoziations, test);
    }

    public getMetaByPdoId(pdoId: string): Property | Association {
        let type = this.typesById[this.selectedType()];
        let test = metaDto => metaDto.PDO_ID == pdoId;

        let filtered = $.extend(
            type.Properties.filter(test),
            type.Assoziations.filter(test)
        );

        return filtered[0] || ({} as any);
    }

    /**
     * Called by UI-Listener with the generic-object holding a DTO with information for this viewModel
     *
     */
    public updateViewModel(genericObject: GenericObject): void {
        this.resetViewModel();
        this.genericObject = genericObject;

        if (!_.contains(Object.keys(genericObject.values), ExtensionEditor)) {
            return;
        }

        let dtoString = genericObject.values[ExtensionEditor]() as string;
        if (dtoString == null) {
            return;
        }

        // wrapping -> values, that are numbers with at least 9 digits <- with '"'
        // this prevents rounding of pdoIds which causes conflicts
        dtoString = dtoString.replace(/([\[:])?(\d{9,})([,\}\]])/g, '$1"$2"$3');

        let json = JSON.parse(dtoString) as MetadataEditor;
        let types = json.Types;

        if (!_.isEmpty(types)) {
            let typeKey = json.SubjectPDTypeMeta;
            let type = types[typeKey];

            this.typesById[typeKey] = type;
            this.dtosByTypeId[typeKey] = json;

            this.updateResourceItemsByErgName(json);
            this.addNewResourceItemsToResourceManager(json);

            this.detailViewGroupsByTypeId[typeKey] =
                this.getDetailViewGroups(json);
            this.listViewColumnsByTypeId[typeKey] =
                this.getListViewColumns(json);

            this.propertiesGrid.metaData = json;
            this.propertyTabViewModel.ResourceItems = json.ResourceItems;
            this.associationTabViewModel.ResourceItems = json.ResourceItems;

            let newSelectedType = String(typeKey);
            let selectedTypeChanged = newSelectedType != this.selectedType();
            if (selectedTypeChanged) {
                this.selectedType(newSelectedType);
            } else {
                this.updateAllViewModels();
            }
        }
    }

    private addNewResourceItemsToResourceManager(dto: MetadataEditor): void {
        _.toArray(dto.ResourceItems)
            .filter(
                (resourceItem: ResourceItem) =>
                    resourceItem.ISO6391 == this.currentLanguage &&
                    resourceItem.Status == 2
            )
            .map((resourceItem: ResourceItem) => {
                return {
                    key: resourceItem.ResourceID,
                    value: resourceItem.Value,
                };
            })
            .forEach(
                (keyValuePair: { key: string; value: string }) =>
                    (ResourceManager.getInstance().resources()[
                        keyValuePair.key
                    ] = keyValuePair.value)
            );
    }

    public updateDtoString() {
        const object = this.dtosByTypeId[this.selectedType()];

        // Fehlererkennung fuer Produktion
        const lvColumnRows =
            this.listViewTabViewModel.ListViewGrid.dataGrid.data() as Array<LVColumnRow>;
        var lvColumns = this.getListViewColumns(object);
        lvColumnRows.forEach((r, index) => {
            var lvColumn = lvColumns[index];
            if (
                r.FilterabilityObs() !== lvColumn.IsFilterable ||
                r.VisibilityObs() !== lvColumn.IsVisible ||
                r.SortabilityObs() !== lvColumn.Sortable
            ) {
                console.warn(
                    'Inconsistent information sent to server',
                    r,
                    lvColumn
                );
            }
        });

        const stringifiedObject = JSON.stringify(object);
        this.genericObject.values[ExtensionEditor](stringifiedObject);
    }

    public syncPropertyGroupCollapsedStatesToLocalStorage() {
        let object = this.dtosByTypeId[this.selectedType()];
        let dv = object.DetailViews[object.SubjectDetailView];
        let dvGroups = dv.DetailViewGroups;

        let dvGroupsWithCollapsedFlagInUIGenerator = dvGroups.filter(group =>
            Utilities.contains(group.UIGenerator, 'IsCollapsed=')
        );
        let partition = _.partition(
            dvGroupsWithCollapsedFlagInUIGenerator,
            group => Utilities.contains(group.UIGenerator, 'IsCollapsed=true')
        );
        let collapsedGroups = partition[0];
        let expandedGroups = partition[1];

        let notify = true;

        collapsedGroups.forEach(group =>
            StorageHelper.set(group.ErgName + '_Collapsed', true, notify)
        );
        expandedGroups.forEach(group =>
            StorageHelper.set(group.ErgName + '_Collapsed', false, notify)
        );
    }

    private validationSubscriptions = [];

    public validate(): boolean {
        this.resetValidationSubscriptions();

        let isErgNameValid = Utilities.isNotBlank(
            this.ErgName.multilineTextBox.value()
        );
        let isErgNamePluralValid = Utilities.isNotBlank(
            this.ErgNamePlural.multilineTextBox.value()
        );
        let rowsWithBlankName = this.getRowsWithBlankName();

        if (
            _.isEmpty(rowsWithBlankName) &&
            isErgNameValid &&
            isErgNamePluralValid
        ) {
            return true;
        }

        let isMandatoryResource = this.loadResource('REQUIRED_CONSTRAINT');

        let validateAsRequired = (viewModel: MultilineTextBoxViewModelT) => {
            let valid = Utilities.isNotBlank(viewModel.value());

            viewModel.isValid(valid);
            viewModel.invalidationText(valid ? '' : isMandatoryResource);

            return valid;
        };

        // validate ErgName
        if (validateAsRequired(this.ErgName.multilineTextBox) === false) {
            let ergNameValidSubscription =
                this.ErgName.multilineTextBox.value.subscribe(value => {
                    let valid = validateAsRequired(
                        this.ErgName.multilineTextBox
                    );
                    if (valid) {
                        ergNameValidSubscription.dispose();
                    }
                });

            this.validationSubscriptions.push(ergNameValidSubscription);
        }

        // validate ErgNamePlural
        if (validateAsRequired(this.ErgNamePlural.multilineTextBox) === false) {
            let ergNamePluralValidSubscription =
                this.ErgNamePlural.multilineTextBox.value.subscribe(value => {
                    let valid = validateAsRequired(
                        this.ErgNamePlural.multilineTextBox
                    );
                    if (valid) {
                        ergNamePluralValidSubscription.dispose();
                    }
                });

            this.validationSubscriptions.push(ergNamePluralValidSubscription);
        }

        // validate rowsWithBlankName
        if (!_.isEmpty(rowsWithBlankName)) {
            rowsWithBlankName.forEach(blankRow => blankRow.isValid(false));
            rowsWithBlankName.forEach(blankRow =>
                this.propertiesGrid
                    .getRow(row => row.PDO_ID == blankRow.PDO_ID)
                    .isValid(false)
            );

            let nameViewModel = this.getPropertyNameViewModel();
            let isCurrentRow = row =>
                row.PDO_ID == this.propertyTabViewModel.DVGItem.PDO_ID;
            let isCurrentRowInvalid = rowsWithBlankName.some(isCurrentRow);
            let getCurrentlySelectedRow = () =>
                this.propertiesGrid.getRow(isCurrentRow);

            if (isCurrentRowInvalid) {
                nameViewModel.isValid(false);
                nameViewModel.invalidationText(isMandatoryResource);
            }

            let onPropertySelectionChanged =
                this.selectedPropertyRows.subscribe(rows => {
                    let currentRowIsValid = !rows.some(row =>
                        rowsWithBlankName.some(
                            blankRow => blankRow.PDO_ID == row.PDO_ID
                        )
                    );

                    nameViewModel.isValid(currentRowIsValid);
                    nameViewModel.invalidationText(
                        currentRowIsValid ? '' : isMandatoryResource
                    );

                    if (_.isEmpty(this.getRowsWithBlankName())) {
                        onPropertySelectionChanged.dispose();
                    }
                });

            let onNameChanged = nameViewModel.value.subscribe(newName => {
                if (Utilities.isNotBlank(newName)) {
                    nameViewModel.isValid(true);
                    nameViewModel.invalidationText('');

                    getCurrentlySelectedRow().isValid(true);
                }

                if (_.isEmpty(this.getRowsWithBlankName())) {
                    onNameChanged.dispose();
                }
            });

            this.validationSubscriptions.push(onPropertySelectionChanged);
            this.validationSubscriptions.push(onNameChanged);
        }

        return false;
    }

    private getRowsWithBlankName(): Array<PropertyRow> {
        return [].concat(this.propertiesGrid.rowsWithBlankName());
    }

    private resetValidationSubscriptions() {
        this.validationSubscriptions.forEach(s => s.dispose());
        this.validationSubscriptions = [];
    }

    public hasValidName(): boolean {
        return !Utilities.isBlank(this.propertyTabViewModel.Name.value());
    }

    public getPropertyNameViewModel(): TextBoxViewModelT {
        return this.propertyTabViewModel.Name;
    }

    private updateResourceItemsByErgName(dto: MetadataEditor): void {
        let resourceItems = this.getResourceItems(dto);

        resourceItems.forEach(item => {
            let language = item.ISO6391;
            let resourceId = item.ResourceID.toLowerCase();

            if (this.resourceItemsByErgName[resourceId] === undefined) {
                this.resourceItemsByErgName[resourceId] = {};
            }

            this.resourceItemsByErgName[resourceId][language] = item;
        });
    }

    private getResourceItems(dto: MetadataEditor): Array<ResourceItem> {
        let resourceItemsById = dto.ResourceItems;
        let resourceItems = Object.keys(resourceItemsById).map(
            key => resourceItemsById[key]
        );

        return resourceItems;
    }

    public updateResourceItem(
        resourceId: string,
        language: string,
        newValue: string
    ) {
        let resourceItem =
            this.dtosByTypeId[this.selectedType()].ResourceItems[
                Utilities.findFirst(
                    Object.keys(
                        this.dtosByTypeId[this.selectedType()].ResourceItems
                    ),
                    key => {
                        let item =
                            this.dtosByTypeId[this.selectedType()]
                                .ResourceItems[key];
                        return (
                            item.ResourceID == resourceId &&
                            item.ISO6391 == language &&
                            item.Status != Status.Deleted
                        );
                    }
                )
            ];

        if (!resourceItem) {
            return;
        }

        resourceItem.Value = newValue;
        Status.setChanged(resourceItem);
    }

    private getListViewColumns(dto: MetadataEditor): Array<LVColumn> {
        return dto.ListViews[dto.SubjectListView].Columns;
    }

    private addProperty() {
        let newMember: Property = {
            PDO_ID: this.getNextPdoId(),
            Status: Status.New,
            ErgName: null,
            ExtensionModuleAlias: 'RADEM',
            Helptext: null,
            IsDynamic: true,
            Name: '',
            SealedMeta: false,
            Indexable: false,
            Tooltip: null,
            Constraint: null,
            ConstraintMessage: null,
            Default: null,
            Derived: false,
            DerivedOverwritable: false,
            DotNetTypeCategory: PropertyTypeCategoryDTO.Default,
            DotNetTypeName: 'System.String',
            Length: '255',
            ObjectTagging: false,
            Required: false,
            RequiredWhen: null,
            Unique: false,
            Unit: null,
        };

        let newItem: DVGItem = {
            PDO_ID: this.getNextPdoId(),
            Status: Status.New,
            ErgName: null,
            ExtensionModuleAlias: 'RADEM',
            Helptext: null,
            IsDynamic: true,
            Name: '',
            SealedMeta: false,
            Tooltip: null,
            ChildNodes: null,
            IsVisible: true,
            IsVisibleOnSummaryView: true,
            ListView: null,
            Member: newMember,
            Parent: null,
            UIGenerator: 'TextBox',
            UIGeneratorSummaryView: 'Label',
            IsEditable: true,
        };

        let newlistViewColumn: LVColumn = {
            PDO_ID: this.getNextPdoId(),
            Status: Status.New,
            ErgName: '',
            ExtensionModuleAlias: 'RADEM',
            Helptext: null,
            IsDynamic: true,
            Name: '',
            SealedMeta: false,
            Tooltip: null,
            ChildNodes: null,
            DotNetTypeName: 'System.String',
            IsAssoziation: false,
            IsFilterable: true,
            IsPDEnumeration: false,
            IsVisible: true,
            ListViewColumnFactory: null,
            Sortable: true,
            UIGenerator: null,
            Unit: null,
            IsEditable: true,
        };

        let selectedType = this.selectedType();
        let dto = this.dtosByTypeId[selectedType];
        let type = this.typesById[selectedType];
        let properties = type.Properties;
        let detailViewGroups = this.detailViewGroupsByTypeId[selectedType];
        let selectedPropertyGroup = this.selectedDetailViewGroupObs();
        let detailViewGroup = detailViewGroups.filter(
            dvGroup =>
                dvGroup.PDO_ID.toString() ==
                selectedPropertyGroup.PDO_ID.toString()
        )[0];
        let listViewColumns = this.listViewColumnsByTypeId[selectedType];

        let listView = dto.ListViews[dto.SubjectListView];

        let resourceItems = dto.ResourceItems;

        properties.push(newMember);
        detailViewGroup.Items.push(newItem);
        listViewColumns.push(newlistViewColumn);

        Status.setChanged(type);
        Status.setChanged(detailViewGroup);
        Status.setChanged(listView);

        let newPropertiesGridRow = this.convertDVGItemToPropertiesRow(newItem);
        newPropertiesGridRow.lvColumn = newlistViewColumn;

        this.propertiesGrid.addRow(newPropertiesGridRow);
        this.propertiesGrid.select([newPropertiesGridRow.PDO_ID]);

        this.propertyTabViewModel.Name.isEnabled(true);
        this.propertyTabViewModel.DVGItem = newItem;
        this.propertyTabViewModel.ResourceItems = resourceItems;

        this.editMaskTabViewModel.DVGItem = newItem;

        const newListViewColumnsRow =
            this.convertLvColumnToListViewRow(newlistViewColumn);
        let dataUpdate = this.listViewTabViewModel.ListViewGrid.dataGrid.data();
        dataUpdate.push(newListViewColumnsRow);
        this.listViewTabViewModel.populateGrid(dataUpdate);
        this.listViewTabViewModel.ListViewGrid.dataGrid.selected([
            newlistViewColumn.PDO_ID,
        ]);

        newPropertiesGridRow.nameFinalizedHandler = (finalName: string) => {
            if (!this.validateName(type, finalName)) {
                this.preventCancel(true);
                return;
            }

            newMember.Name = finalName;
            newMember.ErgName = (
                type.Name +
                '_' +
                finalName +
                '_ERGNAME'
            ).toUpperCase();
            newMember.Helptext = (
                type.Name +
                '_' +
                finalName +
                '_HELPTEXT'
            ).toUpperCase();
            newMember.Tooltip = (
                type.Name +
                '_' +
                finalName +
                '_TOOLTIP'
            ).toUpperCase();
            newMember.ConstraintMessage = (
                type.Name +
                '_' +
                finalName +
                '_CONSTRAINTMESSAGE'
            ).toUpperCase();

            newItem.Name = type.Name + '_' + finalName;
            newPropertiesGridRow.Name = finalName;

            if (newPropertiesGridRow.ErgonomicIdentifierResourceItems) {
                newPropertiesGridRow.ErgonomicIdentifierResourceItems[
                    this.currentLanguage
                ].ResourceID = newMember.ErgName;
            }
            if (newPropertiesGridRow.TooltipResourceItems) {
                newPropertiesGridRow.TooltipResourceItems[
                    this.currentLanguage
                ].ResourceID = newMember.Tooltip;
            }
            if (newPropertiesGridRow.HelptextResourceItems) {
                newPropertiesGridRow.HelptextResourceItems[
                    this.currentLanguage
                ].ResourceID = newMember.Helptext;
            }
            if (newPropertiesGridRow.ConstraintMessageResourceItems) {
                newPropertiesGridRow.ConstraintMessageResourceItems[
                    this.currentLanguage
                ].ResourceID = newMember.ConstraintMessage;
            }

            newlistViewColumn.ErgName = (
                type.Name +
                '_' +
                finalName +
                '_ERGNAME'
            ).toUpperCase();
            newlistViewColumn.Name = finalName;
            this.propertyTabViewModel.updateLVColumnName(newlistViewColumn);
            newListViewColumnsRow.Name = newlistViewColumn.Name;
            newListViewColumnsRow.DisplayName =
                this.convertListViewRowNameToDisplayName(finalName);

            this.propertyTabViewModel.onPropertyNameFinalized();
            this.propertiesGrid.refreshGrid();
            this.listViewTabViewModel.ListViewGrid.dataGrid.data.valueHasMutated();
        };
    }

    private validateName(type: TypeMeta, name: string): boolean {
        let takenNames = _.pluck(type.Properties, 'Name').concat(
            _.pluck(type.Assoziations, 'Name')
        );
        let nameIsAlreadyTaken = _.contains(takenNames, name);

        let callback = () => {
            this.propertyTabViewModel.Name.value('');
            this.propertyTabViewModel.Name.isEnabled(true);
            this.preventCancel(false);
        };

        if (nameIsAlreadyTaken) {
            this.showPropertyAlreadyTakenWarning(name, type.Name, callback);
            return false;
        }

        // Valid tokens (regex is copied from specifier)
        if (!/^[a-zA-Z_]{1}[A-Za-z0-9_]*$/.test(name)) {
            this.showInvalidTokenWarning(name, callback);
            return false;
        }

        return true;
    }

    private createDynamicAssociationCallback(
        name: string,
        aToBName: string,
        bToAName: string,
        targetType: string,
        cardinality_A_To_B: number,
        cardinality_B_To_A: number
    ): void {
        let dto = this.dtosByTypeId[this.selectedType()];

        let dynamicAssociations = dto.DynamicAssoziations;
        let dynamicAssociation =
            dynamicAssociations[
                Utilities.findFirst(
                    Object.keys(dynamicAssociations),
                    key => dynamicAssociations[key].Name == name
                )
            ];

        if (!dynamicAssociation) {
            dynamicAssociation = {} as DynamicAssoziation;
            dynamicAssociation.PDO_ID = this.getNextPdoId();
        }

        dynamicAssociation.Status = Status.New;
        dynamicAssociation.Name = name;
        dynamicAssociation.IsDynamic = true;
        dynamicAssociation.ErgName = null;
        dynamicAssociation.SealedMeta = false;
        dynamicAssociation.Helptext = null;
        dynamicAssociation.Tooltip = null;
        dynamicAssociation.ExtensionModuleAlias = 'RADEM';

        dynamicAssociation.Source =
            dynamicAssociation.Source || ({} as DynamicAssoziationSide);
        dynamicAssociation.Source.PDO_ID = this.getNextPdoId();
        dynamicAssociation.Source.Status = Status.New;
        dynamicAssociation.Source.ErgName = (
            dto.Types[dto.SubjectPDTypeMeta].Name +
            '_' +
            aToBName +
            '_ERGNAME'
        ).toUpperCase();
        dynamicAssociation.Source.Name = aToBName;
        dynamicAssociation.Source.Type = dto.Types[dto.SubjectPDTypeMeta].Name;
        dynamicAssociation.Source.Cardinality = cardinality_A_To_B;
        dynamicAssociation.Source.UniqueObjectList = false;
        dynamicAssociation.Source.OrderedList = false;
        dynamicAssociation.Source.Mandatory = false;
        dynamicAssociation.Source.Indexable = false;
        dynamicAssociation.Source.IncludeSubPrincipals = false;
        dynamicAssociation.Source.IncludeAllPrincipals = false;
        dynamicAssociation.Source.IncludeBasePrincipals = false;

        dynamicAssociation.Target =
            dynamicAssociation.Target || ({} as DynamicAssoziationSide);
        dynamicAssociation.Target.PDO_ID = this.getNextPdoId();
        dynamicAssociation.Target.Status = Status.New;
        dynamicAssociation.Target.ErgName = (
            targetType +
            '_' +
            bToAName +
            '_ERGNAME'
        ).toUpperCase();
        dynamicAssociation.Target.Name = bToAName;
        dynamicAssociation.Target.Type = targetType;
        dynamicAssociation.Target.Cardinality = cardinality_B_To_A;
        dynamicAssociation.Target.UniqueObjectList = false;
        dynamicAssociation.Target.OrderedList = false;
        dynamicAssociation.Target.Mandatory = false;
        dynamicAssociation.Target.Indexable = false;
        dynamicAssociation.Target.IncludeSubPrincipals = false;
        dynamicAssociation.Target.IncludeAllPrincipals = false;
        dynamicAssociation.Target.IncludeBasePrincipals = false;

        dynamicAssociations[dynamicAssociation.PDO_ID] = dynamicAssociation;

        let newDvgItem = {} as DVGItem;
        newDvgItem.ExtensionModuleAlias = 'RADEM';
        newDvgItem.PDO_ID = this.getNextPdoId();
        newDvgItem.Status = Status.New;
        newDvgItem.IsVisible = true;
        newDvgItem.Name = dynamicAssociation.Source.Name;
        newDvgItem.UIGenerator = 'AssoziationGrid';
        newDvgItem.UIGeneratorSummaryView = 'AssoziationGrid';
        newDvgItem.Member = {} as Member;
        newDvgItem.Member.PDO_ID = dynamicAssociation.Source.PDO_ID;
        newDvgItem.Member.Status = Status.New;
        newDvgItem.Member.Name = dynamicAssociation.Source.Name;
        newDvgItem.Member.ErgName = (
            dynamicAssociation.Source.Type +
            '_' +
            dynamicAssociation.Source.Name +
            '_ERGNAME'
        ).toUpperCase();
        newDvgItem.Member.Helptext = (
            dynamicAssociation.Source.Type +
            '_' +
            dynamicAssociation.Source.Name +
            '_HELPTEXT'
        ).toUpperCase();
        newDvgItem.Member.Tooltip = (
            dynamicAssociation.Source.Type +
            '_' +
            dynamicAssociation.Source.Name +
            '_TOOLTIP'
        ).toUpperCase();
        newDvgItem.Member.IsDynamic = true;
        newDvgItem.IsEditable = true;

        newDvgItem.ListView = {} as ListView;
        newDvgItem.ListView.PDO_ID = this.getNextPdoId();
        newDvgItem.ListView.Status = Status.New;
        newDvgItem.ListView.ExtensionModuleAlias = 'RADEM';
        newDvgItem.ListView.Name =
            dynamicAssociation.Source.Type +
            '#' +
            dynamicAssociation.Source.Name;
        newDvgItem.ListView.TypeName = targetType;
        newDvgItem.ListView.AssoziationListView = true;
        newDvgItem.ListView.InheritColumns = true;
        newDvgItem.ListView.ShowButtonSelect = true;
        newDvgItem.ListView.ShowFilter = true;
        newDvgItem.ListView.ShowButtonEdit = true;
        newDvgItem.ListView.ShowButtonDisconnect = true;

        let sourceAssociation = {} as Association;
        sourceAssociation.PDO_ID = dynamicAssociation.Source.PDO_ID;
        sourceAssociation.Status = Status.New;
        sourceAssociation.Name = dynamicAssociation.Source.Name;
        sourceAssociation.ErgName = dynamicAssociation.Source.ErgName;
        sourceAssociation.Cardinality = dynamicAssociation.Source.Cardinality;
        sourceAssociation.IsDynamic = true;
        dto.Types[dto.SubjectPDTypeMeta].Assoziations.push(sourceAssociation);

        let sourceCardinality = dynamicAssociation.Source.Cardinality;
        if (sourceCardinality == Cardinality.One) {
            let newlistViewColumn = {} as LVColumn;
            newlistViewColumn.PDO_ID = this.getNextPdoId();
            newlistViewColumn.Status = Status.New;
            newlistViewColumn.ErgName = newDvgItem.Member.ErgName;
            newlistViewColumn.ExtensionModuleAlias = 'RADEM';
            newlistViewColumn.Helptext = null;
            newlistViewColumn.IsDynamic = true;
            newlistViewColumn.Name =
                newDvgItem.Member.Name + '.ObjectIdentValue';
            newlistViewColumn.SealedMeta = false;
            newlistViewColumn.Tooltip = null;
            newlistViewColumn.IsVisible = true;
            newlistViewColumn.DotNetTypeName =
                'RADFoundation.ProblemDomain.PersistentObject';
            newlistViewColumn.Sortable = true;
            newlistViewColumn.IsFilterable = true;
            newlistViewColumn.Unit = null;
            newlistViewColumn.IsAssoziation = true;
            newlistViewColumn.IsPDEnumeration = false;
            newlistViewColumn.UIGenerator = 'AssociationBox';
            newlistViewColumn.ListViewColumnFactory = null;
            newlistViewColumn.ChildNodes = [];
            newlistViewColumn.IsEditable = true;

            let newListViewColumnsRow =
                this.convertLvColumnToListViewRow(newlistViewColumn);
            this.listViewTabViewModel.ListViewGrid.dataGrid.data.push(
                newListViewColumnsRow
            );
            this.listViewTabViewModel.ListViewGrid.dataGrid.selected([
                newlistViewColumn.PDO_ID,
            ]);

            let selectedType = this.selectedType();
            let listViewColumns = this.listViewColumnsByTypeId[selectedType];
            listViewColumns.push(newlistViewColumn);
        }

        // add new item to current group
        let selectedGroup = this.selectedDetailViewGroupObs();

        let currentDetailViewGroup = this.detailViewGroupsByTypeId[
            this.selectedType()
        ].filter(g => g.PDO_ID == selectedGroup.PDO_ID)[0];
        currentDetailViewGroup.Items.push(newDvgItem);

        if (
            selectedGroup.PDO_ID == selectedGroup.PDO_ID &&
            selectedGroup !== currentDetailViewGroup
        ) {
            selectedGroup.Items.push(newDvgItem);
        }

        // refresh properties grid
        this.onPropertyGroupSelectionChanged([selectedGroup.PDO_ID]);
        this.propertiesGrid.select([newDvgItem.PDO_ID]);
    }

    private showCantCreatePropertyWarning(
        name: string,
        additionalText: string,
        callback: Function
    ) {
        let caption = this.loadResource(
            'RADEM.RADEXTENSIONMANAGER.CANNOTCREATEPROPERTYERROR'
        );
        caption = caption.replace('{0}', name);

        caption += '\r\n' + additionalText;

        // Vorher konnte man das Fenster schlie�en, ohne dass der Callback ausgef�hrt wurde.
        // Da die Ausf�hrung des Callbacks hier wichtig ist, kann das Fenster nur �ber die Schaltfl�che 'OK' geschlossen werden.
        const messageBox = MessageBoxViewModel.createWarningMessageBox(
            caption,
            callback
        );
        messageBox.window.allowClose(false);

        RADApplication.getInstance().showMessageBox(messageBox);
    }

    private showPropertyAlreadyTakenWarning(
        finalName: string,
        typeName: string,
        callback: Function
    ): void {
        let notUnique = this.loadResource(
            'RADEM.RADEXTENSIONMANAGER.NOT_UNIQUE_NAME'
        );
        notUnique = notUnique.replace('{0}', finalName);
        notUnique = notUnique.replace('{1}', typeName);

        this.showCantCreatePropertyWarning(finalName, notUnique, callback);
    }

    private showInvalidTokenWarning(
        finalName: string,
        callback: Function
    ): void {
        let invalidToken = this.loadResource(
            'RADEM.RADEXTENSIONMANAGER.INVALID_TOKEN_CONSTRAINT'
        );

        this.showCantCreatePropertyWarning(finalName, invalidToken, callback);
    }

    private deleteProperty() {
        let selectedRow = this.propertiesGrid.getSelectedRowData()[0];
        let selectedProperty = selectedRow.PDO_ID;
        let selectedDvGroup = this.selectedDetailViewGroupObs();
        let selectedType = this.selectedType();
        let detailViewGroups = this.detailViewGroupsByTypeId[selectedType];
        let detailViewGroup = detailViewGroups.filter(
            x => x.PDO_ID == selectedDvGroup.PDO_ID
        )[0];
        let selectedDvGroupItem = detailViewGroup.Items.filter(
            item => item.PDO_ID == selectedProperty
        )[0];

        // update viewmodel
        this.updateListViewTabViewModelAfterDelete(
            selectedDvGroupItem,
            selectedRow
        );
        this.propertiesGrid.removeSelectedRow();

        // update json
        this.updateListViewAfterDelete(selectedDvGroupItem, selectedRow);
        this.updateDetailViewGroupAfterDelete(
            detailViewGroup,
            selectedDvGroupItem
        );
        this.updateResourceItemsAfterDelete(selectedDvGroupItem);
        this.updatePropertiesAfterDelete(selectedDvGroupItem);
        this.updateAssociationsAfterDelete(selectedDvGroupItem);
        this.updateDynamicAssociationsAfterDelete(
            selectedDvGroupItem,
            selectedRow
        );

        this.genericObject.isModified(true);
    }

    private updateResourceItemsAfterDelete(dvgItem: DVGItem) {
        let propertyOrAssociation = this.getMetaByPdoId(dvgItem.Member.PDO_ID);
        let property = propertyOrAssociation as Property;

        if (this.isAssociationMeta(propertyOrAssociation)) {
            return;
        }

        let ergNames = [
            property.ErgName,
            property.Tooltip,
            property.Helptext,
        ].filter(e => !Utilities.isNullOrUndefined(e));

        this.deleteResourceItems(ergNames);
    }

    private deleteResourceItems(ergNames: Array<string>) {
        if (_.isEmpty(ergNames)) {
            return;
        }

        let json = this.dtosByTypeId[this.selectedType()];
        let allResourceItems = this.getResourceItems(json);

        let associatedResourceItems = allResourceItems.filter(item =>
            _.some(ergNames, ergName =>
                Utilities.equalsIgnoreCase(ergName, item.ResourceID)
            )
        );

        let partitioned = _.partition(
            associatedResourceItems,
            resourceItem => resourceItem.Status == Status.New
        );
        let newItems = partitioned[0];
        let existingItems = partitioned[1];

        // set status to 'deleted' for existing items
        existingItems.forEach(
            resourceItem => (resourceItem.Status = Status.Deleted)
        );

        // remove resourceItems with status 'new'
        allResourceItems = allResourceItems.filter(
            resourceItem =>
                !newItems.some(
                    newItem => newItem.ResourceID == resourceItem.ResourceID
                )
        );

        // remove from resourceItemsByErgName
        associatedResourceItems.forEach(
            item =>
                delete this.resourceItemsByErgName[
                    item.ResourceID.toLowerCase()
                ]
        );
    }

    private updatePropertiesAfterDelete(dvgItem: DVGItem) {
        let propertyOrAssociation = this.getMetaByPdoId(dvgItem.Member.PDO_ID);
        if (Utilities.isNullOrUndefined(propertyOrAssociation)) {
            return;
        }

        let types = this.typesById[this.selectedType()];

        if (!this.isAssociationMeta(propertyOrAssociation)) {
            let property = propertyOrAssociation as Property;
            if (property.Status == Status.New) {
                types.Properties = types.Properties.filter(
                    anyMeta => anyMeta.PDO_ID !== property.PDO_ID
                );
            } else {
                property.Status = Status.Deleted;
            }

            Status.setChanged(types);
        }
    }

    private updateAssociationsAfterDelete(dvgItem: DVGItem) {
        let propertyOrAssociation = this.getMetaByPdoId(dvgItem.Member.PDO_ID);
        if (Utilities.isNullOrUndefined(propertyOrAssociation)) {
            return;
        }

        let types = this.typesById[this.selectedType()];

        if (this.isAssociationMeta(propertyOrAssociation)) {
            let association = propertyOrAssociation as Association;
            if (association.Status == Status.New) {
                types.Assoziations = types.Assoziations.filter(
                    anyMeta => anyMeta.PDO_ID !== association.PDO_ID
                );
            } else {
                association.Status = Status.Deleted;
            }

            Status.setChanged(types);
        }
    }

    private updateDynamicAssociationsAfterDelete(
        dvgItem: DVGItem,
        selectedRow: PropertyRow
    ) {
        let isDynamicAssociation =
            this.propertiesGrid.isDynamicAssociation(selectedRow);
        if (!isDynamicAssociation) {
            return;
        }

        let dto = this.dtosByTypeId[this.selectedType()];

        let dynamicAssociations = dto.DynamicAssoziations;
        let keyToDelete = Utilities.findFirst(
            Object.keys(dynamicAssociations),
            key =>
                dynamicAssociations[key].Source.Name == dvgItem.Name ||
                dynamicAssociations[key].Target.Name == dvgItem.Name
        );

        if (dynamicAssociations[keyToDelete].Status == Status.New) {
            delete dto.DynamicAssoziations[keyToDelete];
        } else {
            dynamicAssociations[keyToDelete].Status = Status.Deleted;
            dynamicAssociations[keyToDelete].Source.Status = Status.Deleted;
            dynamicAssociations[keyToDelete].Target.Status = Status.Deleted;
        }
    }

    private updateListViewAfterDelete(
        dvgItem: DVGItem,
        selectedRow: PropertyRow
    ) {
        let selectedType = this.selectedType();
        let dto = this.dtosByTypeId[selectedType];
        let subjectListView = dto.SubjectListView;
        let listView = dto.ListViews[subjectListView];
        let lvColumnDtos = this.listViewColumnsByTypeId[selectedType];

        let equalsName = (name: string) => (column: LVColumn) =>
            column.Name == name;
        let equalsErgName = (ergName: string) => (column: LVColumn) =>
            column.ErgName == ergName;
        let unequalsPdoId = (id: string) => (column: LVColumn) =>
            column.PDO_ID != id;

        let lvColumnDto =
            selectedRow.lvColumn ||
            Utilities.findFirst(
                lvColumnDtos,
                equalsName(dvgItem.Member.Name)
            ) ||
            Utilities.findFirst(
                lvColumnDtos,
                equalsName(dvgItem.Member.Name + '.ObjectIdentValue')
            ) ||
            Utilities.findFirst(
                lvColumnDtos,
                equalsErgName(dvgItem.Member.ErgName)
            );

        if (lvColumnDto) {
            if (lvColumnDto.Status == Status.New) {
                dto.ListViews[subjectListView].Columns = dto.ListViews[
                    subjectListView
                ].Columns.filter(unequalsPdoId(lvColumnDto.PDO_ID));
                this.listViewColumnsByTypeId[selectedType] =
                    this.getListViewColumns(dto);
            } else {
                lvColumnDto.Status = Status.Deleted;
            }

            Status.setChanged(listView);
        }
    }

    private updateListViewTabViewModelAfterDelete(
        dvgItem: DVGItem,
        selectedRow: PropertyRow
    ) {
        let lvColumnDtos = this.listViewColumnsByTypeId[this.selectedType()];

        let lvColumnDto =
            selectedRow.lvColumn ||
            Utilities.findFirst(
                lvColumnDtos,
                (lvColumnDto: LVColumn) =>
                    lvColumnDto.Name == dvgItem.Member.Name
            ) ||
            Utilities.findFirst(
                lvColumnDtos,
                (lvColumnDto: LVColumn) =>
                    lvColumnDto.Name == dvgItem.Member.Name + 'ObjectIdentValue'
            ) ||
            Utilities.findFirst(
                lvColumnDtos,
                (lvColumnDto: LVColumn) =>
                    lvColumnDto.ErgName == dvgItem.Member.ErgName
            );

        if (lvColumnDto) {
            this.listViewTabViewModel.removeLvColumn(lvColumnDto);
        }
    }

    private updateDetailViewGroupAfterDelete(
        detailViewGroup: DetailViewGroup,
        dvgItem: DVGItem
    ) {
        if (dvgItem.Status == Status.New) {
            detailViewGroup.Items = detailViewGroup.Items.filter(
                item => item.PDO_ID != dvgItem.PDO_ID
            );
        } else {
            dvgItem.Status = Status.Deleted;
        }

        Status.setChanged(detailViewGroup);
    }

    private resetViewModel() {
        this.genericObject = null;
    }

    private resolveErgName(ergName: string, language: string = null) {
        if (!ergName) {
            return '';
        }

        let resourceItem = this.resourceItemsByErgName[ergName.toLowerCase()];
        if (!resourceItem) {
            return '';
        }

        if (!language) language = this.currentLanguage;
        if (!Utilities.isNullOrUndefined(resourceItem[language]))
            return resourceItem[language].Value;

        return '';
    }

    private dotNetTypeLookUp = {
        'RAD.RADFoundation.ProblemDomain.Serial': 'Serial',
        'RAD.RADFoundation.ProblemDomain.MLString': 'Multilingual String',
        'RAD.RADFoundation.ProblemDomain.PDEmbeddedMedium': 'Embedded Medium',
        'RAD.RADFoundation.ProblemDomain.PDEmbeddedFileLink':
            'Embedded File Link',
        'System.Boolean': 'boolean',
        'System.Single': 'float',
        'System.String': 'String',
        'System.Int64': 'int',
        'System.Int32': 'int',
        'System.Byte[]': 'String',
    };

    private getPropertyByLvColumn(column: LVColumn): Property {
        let dto = this.dtosByTypeId[this.selectedType()];
        let index = _.findIndex(
            dto.Types[dto.SubjectPDTypeMeta].Properties,
            e => e.ErgName == column.ErgName
        );

        return dto.Types[dto.SubjectPDTypeMeta].Properties[index];
    }

    private lookupDotNetTypeName(type: string): string {
        if (Utilities.isNullOrUndefined(type)) return '';

        let dotNetTypeName = this.lookupRawDotNetTypeName(type);
        if (Utilities.contains(type.toLowerCase(), 'nullable')) {
            dotNetTypeName = `Nullable<${dotNetTypeName}>`;
        }
        return dotNetTypeName;
    }

    private lookupRawDotNetTypeName(type: string): string {
        if (this.dotNetTypeLookUp[type] !== undefined) {
            return this.dotNetTypeLookUp[type];
        }
        if (type.indexOf('System.DateTime') > -1) {
            return 'DateTime';
        }
        if (type.indexOf('System.Single') > -1) {
            return 'float';
        }
        if (type.indexOf('.') > -1) {
            let split = type.split('.');
            return split[split.length - 1];
        }
        return type;
    }

    private getCurrentDetailView(): DetailView {
        let selectedType = this.selectedType();
        let dto = this.dtosByTypeId[selectedType];
        let subjectDetailView = dto.SubjectDetailView;
        let detailView = dto.DetailViews[subjectDetailView];

        return detailView;
    }

    private getDetailViewGroups(dto: MetadataEditor): Array<DetailViewGroup> {
        let detailView = dto.DetailViews[dto.SubjectDetailView];

        return this.getAllDetailViewGroups(detailView.DetailViewGroups);
    }

    private getAllDetailViewGroups(
        groups: Array<DetailViewGroup>
    ): Array<DetailViewGroup> {
        return TreeUtilities.getAllChildren(groups, 'ChildNodes');
    }

    private getAllNodes(nodes: Array<NodeData>): Array<NodeData> {
        return TreeUtilities.getAllChildren(nodes, 'children');
    }

    private loadResource(resourceId: string): string {
        return ResourceManager.getInstance().get(resourceId);
    }
}

export default ExtensionEditorViewModel;
ComponentManager.setViewModel(
    'ExtensionEditorViewModel',
    ExtensionEditorViewModel,
    'RADExtensionManagerHTMLViewExtension'
);
